<script setup lang="ts">
import type { ResourceRole } from '@/backend/types'
import { updateProject } from '@/backend/updateProject'
import type {
  AssignableRole,
  ResourceAccessType,
  ResourceMember,
} from '@/modules/IdentityAndAccess/types'
import { useProjectCover } from '@/modules/Project/useProjectCover'
import ProjectCoverMenu from '@/modules/Projects/ProjectCoverMenu.vue'
import { useProjects } from '@/modules/Projects/useProjects'
import {
  useWorkspaceMembers,
  type WorkspaceMember,
} from '@/modules/WorkspaceSettings/useWorkspaceMembers'
import { toast } from '@/shared/toast'
import { assertIsNotNullOrUndefined, exhaustiveGuard } from '@/shared/utils/typeAssertions'
import AutosizedTextarea from '@/sharedComponents/AutosizedTextarea.vue'
import ScrollGradient from '@/sharedComponents/ScrollGradient.vue'
import AvatarIcon from '@/uiKit/AvatarIcon.vue'
import CircularProgress from '@/uiKit/CircularProgress.vue'
import { computed, ref, useTemplateRef, watch } from 'vue'
import PermissionsDialog from './PermissionsDialog.vue'
import PermissionsDialogShare from './PermissionsDialogShare.vue'
import { useProjectPermissionsStore } from './projectPermissionsStore'

const props = defineProps<{
  workspaceId: string
  open: boolean
  rootProjectId: string
}>()

const emit = defineEmits<{ (e: 'close'): void }>()

const projectsStore = useProjects()
const rootProject = computed(() => projectsStore.getProjectById(props.rootProjectId))

const url = computed(
  () => `${window.location.host}/${props.workspaceId}/projects/${props.rootProjectId}`,
)

const workspaceMembersStore = useWorkspaceMembers()

const projectPermissionsStore = useProjectPermissionsStore()
const onInviteUsers = async (userIds: string[], role: AssignableRole) => {
  if (role !== 'reader' && role !== 'editor') {
    return
  }

  const results = await Promise.all(
    userIds.map((userId) =>
      projectPermissionsStore.setProjectRole({
        projectId: props.rootProjectId,
        role,
        userId,
        workspaceId: props.workspaceId,
      }),
    ),
  )

  if (results.some((result) => !result.ok)) {
    toast.error('Failed to invite some users, please try again.')
    throw new Error('Failed to invite users')
  }
}

type ProjectMember = WorkspaceMember & {
  projectRole: ResourceRole
  isCurrentUser: boolean
  type: 'workspaceMember'
}

const projectMembers = computed<ResourceMember[]>(() =>
  Object.entries(projectPermissionsStore.workspaceMemberRoles)
    .map(([userId, role]) => {
      const workspaceMember = workspaceMembersStore.workspaceMembers.find((m) => m.id === userId)
      // Ideally, all agent members would also be workspace members. But there is a BE bug where
      // agent members aren't deleted when a workspace member is deleted, so we could get
      // orphaned agent members. This check is to handle that case - we filter them out
      // on the FE.
      // see https://vseven.slack.com/archives/C06UK9537B8/p1725526371109919
      if (!workspaceMember) {
        return null
      }

      return {
        ...workspaceMember,
        role,
      }
    })
    .filter((member): member is ProjectMember => !!member?.role),
)

const onChangeRole = (userId: string, role: ResourceRole | null) => {
  if (role === null || role === 'editor' || role === 'reader') {
    try {
      projectPermissionsStore.setProjectRole({
        projectId: props.rootProjectId,
        userId,
        workspaceId: props.workspaceId,
        role,
      })
    } catch {
      toast.error('Failed to update role')
      throw new Error('Failed to update role')
    }
  }
}

const updateProjectDefaultRole = (accessType: ResourceAccessType) => {
  let role: 'editor' | 'reader' | null
  if (accessType === 'public') {
    role = 'editor'
  } else if (accessType === 'read_only') {
    role = 'reader'
  } else {
    role = null
  }

  projectPermissionsStore.setProjectRole({
    userId: 'anyone_in_workspace',
    role,
    workspaceId: props.workspaceId,
    projectId: props.rootProjectId,
  })
}
const projects = useProjects()

const localName = ref('')
const localDescription = ref('')

watch(
  () => props.open,
  (open) => {
    if (open) {
      localName.value = rootProject.value?.name || ''
      localDescription.value = rootProject.value?.description || ''
    }
  },
  { immediate: true },
)

const hasError = ref(false)

const inFlightUpdate = ref<ReturnType<typeof updateProject>>()

const isDirty = computed(
  () =>
    localName.value !== rootProject.value?.name ||
    localDescription.value !== rootProject.value?.description,
)

const revert = async (e: Event) => {
  await inFlightUpdate.value
  localName.value = rootProject.value?.name || ''
  localDescription.value = rootProject.value?.description || ''
  e.stopPropagation()
}

const saving = computed(() => !!inFlightUpdate.value)

const save = async () => {
  if (!isDirty.value) {
    return
  }

  hasError.value = false

  const name = localName.value.trim() === '' ? null : localName.value.trim()
  const description = localDescription.value.trim() === '' ? null : localDescription.value.trim()

  if (name === null || name.length < 3) {
    hasError.value = true
    toast.error('Agent name must be at least 3 characters long')
    return
  }

  if (description === null || description.length < 3) {
    hasError.value = true
    toast.error('Please describe what this agent should be used for')
    return
  }

  await inFlightUpdate.value

  assertIsNotNullOrUndefined(rootProject.value)
  const project = rootProject.value

  try {
    inFlightUpdate.value = updateProject(project.workspaceId, project.id, {
      name,
      description,
    })
    const result = await inFlightUpdate.value
    inFlightUpdate.value = undefined

    if (!result.ok) {
      hasError.value = true
      return
    }

    projects.updateProject(project.id, {
      name: result.data.name,
      description: result.data.description,
    })
  } catch {
    inFlightUpdate.value = undefined
    hasError.value = true
    return
  }
}

const saveAndClose = async () => {
  await inFlightUpdate.value
  await save()

  if (hasError.value) {
    return
  }

  emit('close')
}

const { uploadFileAsProjectCover, getRandomProjectCoverURL, fetchCoverFile } = useProjectCover()

const setRandomProjectCover = async () => {
  const url = getRandomProjectCoverURL()
  const file = await fetchCoverFile(url)
  if (file) {
    uploadFileAsProjectCover(file, props.workspaceId, props.rootProjectId)
  }
}

const descriptionContainer = useTemplateRef('descriptionContainer')

const anyOneInWorkspaceProjectPermission = computed<ResourceAccessType>(() => {
  switch (projectPermissionsStore.defaultRole) {
    case 'reader':
    case 'reviewer':
      return 'read_only'
    case 'editor':
    case 'owner':
    case 'admin':
      return 'public'
    case 'worker':
    case null:
      return 'private'
    default:
      return exhaustiveGuard(projectPermissionsStore.defaultRole, 'Unhandled default role')
  }
})
</script>

<template>
  <PermissionsDialog
    :root-project-id="rootProjectId"
    :workspace-id="workspaceId"
    :open="open"
    :members="projectMembers"
    :disable-close="saving"
    :access-type="anyOneInWorkspaceProjectPermission"
    resource-type="project"
    @close="saveAndClose"
    @change:user-role="onChangeRole"
    @change:access-type="updateProjectDefaultRole"
    @invite="onInviteUsers"
  >
    <template #heading="{ dialogId }">
      <div class="mb-3 flex w-full flex-row items-center justify-start">
        <!-- avatar, name, description, & close -->
        <div
          class="relative rounded-full border-2 border-dashed border-background-border-default p-0.5"
        >
          <div
            v-if="rootProject?.coverImageUploading"
            class="flex size-[64px] items-center justify-center"
          >
            <CircularProgress
              size="xs"
              class="animate-spin"
            />
          </div>
          <AvatarIcon
            v-else
            :full-text="rootProject?.name"
            size="xl"
            shape="circle"
            :url="rootProject?.coverImageUrls.high"
            :loading-condition="rootProject?.coverImageUploading ?? false"
            :error-condition="rootProject?.coverImageDownloadError"
            alt="Agent cover"
          />
          <div
            class="absolute -bottom-1.5 -right-1.5 flex items-center rounded-corner-12 bg-surface-primary p-0.5 shadow-xs"
          >
            <ProjectCoverMenu
              :teleport-target-id="dialogId"
              @update-cover="$event && uploadFileAsProjectCover($event, workspaceId, rootProjectId)"
              @generate-cover="setRandomProjectCover"
            />
          </div>
        </div>
      </div>
      <div class="flex flex-col">
        <!-- name and description -->
        <AutosizedTextarea
          v-model="localName"
          mode="singleline"
          placeholder="Add a name"
          class="bg-background-transparent py-1 text-display-xs-20px-default outline-none"
          aria-label="Agent name"
          maxlength="200"
          @keydown.escape="isDirty && revert($event)"
          @blur="save"
          @submit="save"
        />
        <div class="relative z-0 -translate-x-2">
          <ScrollGradient
            location="top"
            :container="descriptionContainer"
            size="md"
            class="from-background-transparent to-surface-tertiary-persist"
          />
          <div
            ref="descriptionContainer"
            class="go-scrollbar relative max-h-40 overflow-y-auto rounded-corner-8 outline-none transition-all focus-within:bg-background-transparent-hovered hover:bg-background-transparent-hovered"
          >
            <AutosizedTextarea
              v-model="localDescription"
              mode="multiline"
              placeholder="Add a description"
              class="rounded-corner-8 bg-background-transparent px-2 py-1.5 text-sm-12px-light outline-none"
              aria-label="Agent description"
              @keydown.escape="isDirty && revert($event)"
              @blur="save"
              @submit="save"
            />
          </div>
          <ScrollGradient
            location="bottom"
            :container="descriptionContainer"
            size="md"
            class="from-background-transparent to-surface-tertiary-persist"
          />
        </div>
      </div>
    </template>
    <template #share="{ labelId }">
      <PermissionsDialogShare
        :url="url"
        :label-id="labelId"
      />
    </template>
  </PermissionsDialog>
</template>
